import { useState } from "react";
import "@/App.css";
import StepIndicator from "@/components/StepIndicator";
import Step1Upload from "@/components/Step1Upload";
import Step2Preview from "@/components/Step2Preview";
import Step3Download from "@/components/Step3Download";
import ErrorBanner from "@/components/ErrorBanner";

/**
 * API base URL for the hosted Flask backend.
 * Swap this value when the Railway deployment is live.
 * NOTE: The Flask API must have CORS enabled for this frontend's origin
 * before requests will succeed from the browser.
 */
export const API_BASE_URL = "https://jubilant-dream-production-9950.up.railway.app";

function App() {
  const [step, setStep] = useState(1);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [error, setError] = useState("");

  const handleAnalysed = (result) => {
    setAnalysisResult(result);
    setJobId(result?.job_id || null);
    setError("");
    setStep(2);
  };

  const handleGenerated = () => {
    setError("");
    setStep(3);
  };

  const handleReset = () => {
    setAnalysisResult(null);
    setJobId(null);
    setError("");
    setStep(1);
  };

  return (
    <div className="App min-h-screen bg-white" data-testid="ppc-app-root">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white border-b-2 border-[#0d0d0d]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <svg
              viewBox="0 0 2001 2000"
              aria-hidden="true"
              className="ss-mark shrink-0"
              data-testid="app-logo"
            >
              <path className="m" d="M1164.56,496.98l-603.68,159.89v254.54l947.4-251.32v70.95l-507.75,134.49,507.75,134.48h.05s0,395.6,0,395.6l-1015.59,268.98v-395.62l507.74-134.47-507.75-134.47h-.04v-395.63l671.88-177.94v70.53ZM560.92,1321.44v254.55l879.21-232.86v-254.54l-879.21,232.86ZM1508.27,660.09l-343.72-233.64,343.72-91.03v324.67Z" />
              <path className="a" d="M1508.32,660.09l-343.73-233.64,343.73-91.03v324.67Z" />
            </svg>
            <span className="font-['Archivo'] font-extrabold text-[17px] lowercase text-[#0d0d0d] leading-none truncate">
              sellers side
            </span>
            <span className="font-brand-mono text-xs text-[#565b57] leading-none hidden sm:inline">
              ppc optimiser
            </span>
          </div>
          <a
            href="https://www.skool.com/sellers-side/classroom/26b93931?md=8126f6ec7220410ba7f3b653d9bb1d11"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary !px-6 !py-2.5 !text-xs uppercase tracking-wide shrink-0"
            data-testid="tutorial-link"
          >
            See The Tutorial →
          </a>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 pb-12">
        {/* Hero */}
        <section className="pt-12 sm:pt-16">
          <p className="kicker">Amazon PPC · Bulk Workflow</p>
          <h1
            className="display text-5xl sm:text-6xl lg:text-7xl text-[#0d0d0d] mt-5"
            data-testid="app-title"
          >
            PPC Optimiser
          </h1>
          <p className="text-[#565b57] text-base mt-5 max-w-xl leading-relaxed">
            Upload your Seller Central exports, preview every proposed change, then
            download an Amazon-ready bulk file.
          </p>
        </section>

        <div className="mt-10">
          <StepIndicator currentStep={step} />
        </div>

        {error && <ErrorBanner message={error} onDismiss={() => setError("")} />}

        <main className="mt-8">
          {step === 1 && (
            <Step1Upload
              apiBaseUrl={API_BASE_URL}
              onAnalysed={handleAnalysed}
              onError={setError}
            />
          )}
          {step === 2 && (
            <Step2Preview
              apiBaseUrl={API_BASE_URL}
              jobId={jobId}
              result={analysisResult}
              onGenerated={handleGenerated}
              onError={setError}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && (
            <Step3Download
              apiBaseUrl={API_BASE_URL}
              jobId={jobId}
              onReset={handleReset}
              onError={setError}
            />
          )}
        </main>

        <footer className="mt-20 pt-6 border-t-2 border-[#0d0d0d] font-brand-mono text-[11px] uppercase tracking-[2px] text-[#565b57]">
          Built for Amazon sellers · Review all changes before uploading to Campaign Manager
        </footer>
      </div>
    </div>
  );
}

export default App;
